import React, { useCallback, useEffect, useState } from 'react';

import { Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCircleCheckFilled, IconCircleOff } from '@tabler/icons-react';
import PackageStatusBadge from 'components/status-badges/PackageStatusBadge';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthletePackages from 'hooks/athletePackage/useAthletePackages';
import AthletePackageDetail from 'pages/private/owner/AthleteDetail/components/Packages/components/AthletePackageDetail';
import { CellProps } from 'react-table';
import { AthletePackage, AthletePackageList, AthleteRetrieve } from 'types';
import { getCreditDispensationsTotalCredits } from 'utils/athlete-package.helpers';

interface AthletePackageSearchFilters {
  statuses: AthletePackage['status'][];
}

const AthletePackagesTable = ({
  athlete,
  searchFilters,
  permission = true,
}: {
  athlete: AthleteRetrieve;
  searchFilters: AthletePackageSearchFilters;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency, dateFormat } = useLocalization();

  const [athletePackageId, setAthletePackageId] = useState<string | undefined>();
  const [isViewFormOpened, { open: openViewForm, close: closeViewForm }] = useDisclosure(false);
  const [tableData, setTableData] = useState<AthletePackageList[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthletePackages({
      athlete: athlete.athleteId,
      ...searchFilters,
    });

  const handlePackageClick = useCallback(
    (ap: string): void => {
      setAthletePackageId(ap);
      openViewForm();
    },
    [openViewForm],
  );

  const handleClose = (): void => {
    closeViewForm();
    setAthletePackageId(undefined);
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Status',
      accessor: 'status' as const,
      Cell: ({ value }: CellProps<AthletePackageList>) => (
        <PackageStatusBadge status={value} isDesktop={false} />
      ),
    },
    {
      Header: 'Name',
      accessor: 'package.name' as const,
      Cell: ({ value, row }: CellProps<AthletePackageList>) => (
        <Text
          data-cy='checkAthletePackageName'
          weight='bold'
          fs='italic'
          w='fit-content'
          underline
          sx={{ cursor: 'pointer', wordBreak: 'break-word' }}
          onClick={() => handlePackageClick(row.original.athletePackageId)}
        >
          {value}
        </Text>
      ),
    },
    {
      Header: 'Total',
      accessor: 'billingPlan.totalPrice' as const,
      Cell: ({ value }: CellProps<AthletePackageList>) => (
        <div data-cy='checkBPTotalPriceAfterAthletePackage'>{formatCurrency(value / 100)}</div>
      ),
    },
    {
      Header: 'Package Dates',
      Cell: ({ row }: CellProps<AthletePackageList>) => {
        const limitedCredits =
          row.original.package.type === 'TIME_OR_CREDIT_BASED'
            ? getCreditDispensationsTotalCredits(row.original.creditDispensations)
            : 0;
        const endDate = row.original.cancellationDate || row.original.endDate;

        return (
          <div>
            {dayjs(row.original.startDate).format(dateFormat)}
            <div />
            {endDate ? dayjs(endDate).format(dateFormat) : '-'}
            {!!limitedCredits && <Text>or {limitedCredits} Credits</Text>}
            {row.original.cancellationDate && <Text>Early Cancellation</Text>}
          </div>
        );
      },
    },
    {
      Header: 'Renew',
      accessor: 'autoRenew' as const,
      Cell: ({ value }: CellProps<AthletePackageList>) =>
        value ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.md} color='gray' />
        ),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        margin={0}
        bgc='gray'
      />
      <AthletePackageDetail
        athletePackageId={athletePackageId}
        setAthletePackageId={setAthletePackageId}
        opened={isViewFormOpened}
        closeHandler={handleClose}
        permission={permission}
      />
    </>
  );
};

export default AthletePackagesTable;
